import { Controller } from 'stimulus'
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
Chart.register(DoughnutController, ArcElement, Tooltip, Legend, Title);

export default class extends Controller {
  static targets = ['messagesChart', 'progressChart', 'tasksChart', 'likesChart']

  connect() {
    this._renderLikesChart();
    this._renderMessagesChart();
    this._renderProgressChart();
    this._renderTasksStatusChart();
  }

  _renderMessagesChart() {
    if (!this.hasMessagesChartTarget) return;

    const emptyData = JSON.parse(this.messagesChartTarget.dataset.empty);
    const chartData = JSON.parse(this.messagesChartTarget.dataset.chart);

    const {
      center_labels: {
        primary: { value: centerLabel } = {},
        secondary: { value: centerSubLabel, color: centerSubLabelColor } = {}
      } = {}
    } = chartData;

    let values = [], labels = [], colors = [];

    for (const key of ['social', 'ai_assistant', 'in_task']) {
      const segment = chartData[key];
      if (segment && segment.value != null) {
        values.push(segment.value);
        labels.push(segment.label);
        colors.push(segment.color);
      }
    }

    const total = values.reduce((sum, val) => sum + val, 0);
    if (total === 0) {
      values = emptyData.values;
      colors = emptyData.colors;
      labels = [];
    }

    const doughnutChartOptions = {
      dataValues: values,
      labels,
      labelColors: colors,
      centerLabel,
      centerSubLabel,
      centerSubLabelColor
    };

    this._renderDoughnutChart(this.messagesChartTarget, doughnutChartOptions);
  }

  _renderProgressChart() {
    if (!this.hasProgressChartTarget) return;

    const chartData = JSON.parse(this.progressChartTarget.dataset.chart);

    const {
      progress: { label: progressLabel, value: progressValue, color: progressColor },
      remainder: { label: remainderLabel, value: remainderValue, color: remainderColor },
      center_labels: {
        primary: { value: centerLabel },
        secondary: { value: centerSubLabel, color: centerSubLabelColor },
      },
      expected_progress
    } = chartData;

    let dataValues, labels, labelColors;

    if (expected_progress) {
      const {
        label: expectedLabel,
        value: expectedValue,
        color: expectedColor,
      } = expected_progress;

      dataValues = [progressValue, expectedValue, remainderValue];
      labels = [progressLabel, expectedLabel, remainderLabel];
      labelColors = [progressColor, expectedColor, remainderColor];
    } else {
      dataValues = [progressValue, remainderValue];
      labels = [progressLabel, remainderLabel];
      labelColors = [progressColor, remainderColor];
    }

    const displayPercentage = true;

    const doughnutChartOptions = {
      dataValues,
      labels,
      labelColors,
      centerLabel,
      centerSubLabel,
      centerSubLabelColor,
      displayPercentage
    };

    this._renderDoughnutChart(this.progressChartTarget, doughnutChartOptions);
  }

  _renderTasksStatusChart() {
    if (!this.hasTasksChartTarget) return;

    const emptyData = JSON.parse(this.tasksChartTarget.dataset.empty);
    const chartData = JSON.parse(this.tasksChartTarget.dataset.chart);

    const {
      unstarted: { label: unstartedLabel, value: unstartedValue, color: unstartedColor } = {},
      in_progress: { label: inProgressLabel, value: inProgressValue, color: inProgressColor } = {},
      completed: { label: completedLabel, value: completedValue, color: completedColor } = {},
      center_labels: {
        primary: { value: centerLabel } = {},
        secondary: { value: centerSubLabel, color: centerSubLabelColor } = {},
      } = {}
    } = chartData;

    let dataValues = [completedValue, inProgressValue, unstartedValue];
    let labels = [completedLabel, inProgressLabel, unstartedLabel];
    let labelColors = [completedColor, inProgressColor, unstartedColor];

    const total = dataValues.reduce((sum, val) => sum + val, 0);
    if (total === 0) {
      dataValues = emptyData.values;
      labelColors = emptyData.colors;
      labels = [];
    }

    const doughnutChartOptions = {
      dataValues,
      labels,
      labelColors,
      centerLabel,
      centerSubLabel,
      centerSubLabelColor,
    };

    this._renderDoughnutChart(this.tasksChartTarget, doughnutChartOptions);
  }

  _renderLikesChart() {
    if (!this.hasLikesChartTarget) return;

    const emptyData = JSON.parse(this.likesChartTarget.dataset.empty);
    const chartData = JSON.parse(this.likesChartTarget.dataset.chart);

    const {
      given: { label: givenLabel, value: givenValue, color: givenColor } = {},
      received: { label: receivedLabel, value: receivedValue, color: receivedColor } = {},
      center_labels: {
        primary: { value: centerLabel } = {},
        secondary: { value: centerSubLabel, color: centerSubLabelColor } = {}
      } = {}
    } = chartData;

    let values = [givenValue, receivedValue];
    let labels = [givenLabel, receivedLabel];
    let colors = [givenColor, receivedColor];

    const total = values.reduce((sum, val) => sum + val, 0);
    if (total === 0) {
      values = emptyData.values;
      colors = emptyData.colors;
      labels = [];
    }

    const doughnutChartOptions = {
      dataValues: values,
      labels,
      labelColors: colors,
      centerLabel,
      centerSubLabel,
      centerSubLabelColor
    };

    this._renderDoughnutChart(this.likesChartTarget, doughnutChartOptions);
  }


  _renderDoughnutChart(target, options = {}) {
    const canvas = target.getContext('2d');

    const data = {
      labels: options.labels,
      datasets: [{
        data: options.dataValues,
        backgroundColor: options.labelColors,
        borderWidth: 0
      }]
    };

    const config = {
      type: 'doughnut',
      data: data,
      options: {
        cutout: '80%',
        rotation: 210,        // Start at 7 o'clock
        circumference: 300,   // End at 5 o'clock
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.raw;

                return options.displayPercentage ? `${value}%` : value;
              }
            },
            filter: function(context) {
              // Prevents tooltip from showing at all if label is empty
              return !!context.label && context.label.trim() !== '';
            }
          }
        }
      },
      plugins: [{ // Custom plugin to draw text in the center
        id: 'centerText',
        beforeDraw(chart) {
          const { width, height } = chart;
          const ctx = chart.ctx; // canvas context
          ctx.save();

          const lines = [options.centerLabel, options.centerSubLabel];
          const fontSizes = [36, 16]; // top line larger
          const fontWeights = ['bold', 'normal']; // top line bold
          const colors = ['#595959', options.centerSubLabelColor];
          const lineSpacing = 6;

          // Calculate total text block height
          const totalHeight = fontSizes[0] + fontSizes[1] + lineSpacing;

          let offsetY = 8; // Adjust this value to move it further down
          let y = (height - totalHeight) / 2 + offsetY;

          lines.forEach((text, i) => {
            ctx.font = `${fontWeights[i]} ${fontSizes[i]}px sans-serif`;
            ctx.fillStyle = colors[i];
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(text, width / 2, y);

            y += fontSizes[i] + lineSpacing;
          });

          ctx.restore();
        }
      }]
    };

    new Chart(canvas, config);
  }
}
